/**
 * AdminNotizieBadgeFloating (feb-2026 Step 5)
 *
 * Badge globale flottante mostrato SOLO su rotte `/admin/*` (esclusa già /admin/notizie).
 * Polling 60s all'endpoint `/api/admin/notizie/pending-count`.
 * Click → naviga a `/admin/notizie` sul tab "in_review".
 *
 * Silent-fail se l'utente non è autenticato staff (401/403 → non renderizza).
 */
import { useEffect, useState } from "react";
import { useLocation, Link } from "react-router-dom";
import axios from "axios";
import { Newspaper } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const POLL_MS = 60_000;

export default function AdminNotizieBadgeFloating() {
  const location = useLocation();
  const [count, setCount] = useState(0);
  const [visible, setVisible] = useState(false);

  const isAdminRoute =
    location.pathname.startsWith("/admin") &&
    !location.pathname.startsWith("/admin/notizie") &&  // già in pagina dedicata
    !location.pathname.startsWith("/admin/login");

  useEffect(() => {
    if (!isAdminRoute) { setVisible(false); return; }
    let cancelled = false;
    const fetchCount = async () => {
      try {
        const { data } = await axios.get(`${API}/admin/notizie/pending-count`, { withCredentials: true });
        if (cancelled) return;
        const c = data?.in_review || 0;
        setCount(c);
        setVisible(c > 0);
      } catch {
        // 401/403 → utente non staff, nascondi silenziosamente
        if (!cancelled) setVisible(false);
      }
    };
    fetchCount();
    const iv = setInterval(fetchCount, POLL_MS);
    return () => { cancelled = true; clearInterval(iv); };
  }, [isAdminRoute]);

  if (!isAdminRoute || !visible) return null;

  return (
    <Link
      to="/admin/notizie"
      className="fixed bottom-6 right-6 z-40 flex items-center gap-2 px-4 py-2.5 rounded-full bg-amber-500 hover:bg-amber-600 text-white shadow-lg transition-all focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-300 focus-visible:ring-offset-2"
      aria-label={`${count} notizie in attesa di revisione — apri workflow editoriale`}
      data-testid="admin-notizie-floating-badge"
    >
      <Newspaper className="w-4 h-4" aria-hidden="true" />
      <span className="text-sm font-medium">Notizie da revisionare</span>
      <span className="inline-flex items-center justify-center min-w-6 h-6 px-2 rounded-full bg-white text-amber-700 text-xs font-bold" aria-hidden="true">
        {count}
      </span>
    </Link>
  );
}
